// FILE: src/components/FlowBuilder/properties/ActionProperties.jsx
import React, { useState, useEffect } from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Textarea,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    useToast,
    Tag,
    Flex,
    Radio,
    RadioGroup,
    Code,
    IconButton,
    Select as ChakraSelect,
} from '@chakra-ui/react';
import Select from 'react-select';
import {
    LuMessageCircle,
    LuMessageSquare,
    LuMail,
    LuBell,
    LuPhone,
    LuZap,
    LuDatabase,
    LuMerge,
    LuBraces,
} from 'react-icons/lu';
import API from '../../../helpers/axiosInstance';
import { getApiHeaders } from '../../../utils/DefaultFunction';
import VariablePickerDrawer from '../VariablePickerDrawer';

const Channels = [
    {
        name: "Whatsapp",
        icon: LuMessageCircle,
        color: "#00a63e",
        description: "Rich messaging with media and buttons",
        id: 2,
    },
    {
        name: "SMS",
        icon: LuMessageSquare,
        color: "#155dfc",
        description: "Simple text messages",
        id: 1,
    },
    {
        name: "E-Mail",
        icon: LuMail,
        color: "#9810fa",
        description: "HTML emails with rich content",
        id: 3,
    },
    {
        name: "Push Notification",
        icon: LuBell,
        color: "#e60076",
        description: "Mobile app notifications",
        id: 6,
    },
    {
        name: "In-App Notification",
        icon: LuBell,
        color: "#4f39f6",
        description: "In-app messages and alerts",
        id: 5,
    },
    {
        name: "Voice (IVR)",
        icon: LuPhone,
        color: "#f54a00",
        description: "Automated voice calls",
        id: 4,
    },
];

// Helper function to extract variables from template with $variable syntax
const extractTemplateVariables = (template) => {
    if (!template) return [];
    const regex = /\$(\w+)/g;
    const matches = [...template.matchAll(regex)];
    const uniqueVars = [...new Set(matches.map(match => match[1]))];
    return uniqueVars;
};

const DEFAULT_MAPPING = {
    sourceType: 'fixed',
    sourceValue: '',
    eventName: '',
    propertyName: '',
    fallbackValue: '',
};

export default function ActionProperties({
    nodeConfig,
    setNodeConfig,
    updateNodeData,
    setSelectedNode,
    nodes = [],
}) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [variablePickerOpen, setVariablePickerOpen] = useState(false);
    const [activeVariableKey, setActiveVariableKey] = useState(null);
    const toast = useToast();

    useEffect(() => {
        fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Pre-select template and set available channels when component loads with existing nodeConfig
    useEffect(() => {
        if (nodeConfig.templateId && templates?.data?.length > 0) {
            const template = templates.data.find((t) => t.id === parseInt(nodeConfig.templateId));
            if (template && (!selectedTemplate || selectedTemplate.id !== template.id)) {
                setSelectedTemplate(template);

                if (!nodeConfig.availableChannels || nodeConfig.availableChannels.length === 0) {
                    const channelIds = template.channel_id ? JSON.parse(template.channel_id) : [];
                    const availableChannels = Channels.filter(ch => channelIds.includes(ch.id));
                    setNodeConfig({
                        ...nodeConfig,
                        availableChannels,
                        channelIds,
                    });
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates?.data?.length, nodeConfig.templateId]);

    // Initialize variableMappings when template changes
    useEffect(() => {
        if (nodeConfig.template && nodeConfig.messageType === 'template') {
            const variables = extractTemplateVariables(nodeConfig.template);
            const currentMappings = nodeConfig.variableMappings || {};
            const newMappings = { ...currentMappings };

            variables.forEach(variable => {
                if (!newMappings[variable]) {
                    newMappings[variable] = { ...DEFAULT_MAPPING };
                }
            });

            Object.keys(newMappings).forEach(key => {
                if (!variables.includes(key)) {
                    delete newMappings[key];
                }
            });

            if (JSON.stringify(currentMappings) !== JSON.stringify(newMappings)) {
                setNodeConfig(prev => ({
                    ...prev,
                    variableMappings: newMappings
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nodeConfig.template, nodeConfig.messageType]);

    const fetchTemplates = async () => {
        setLoadingTemplates(true);
        try {
            const response = await API.get(
                `${process.env.REACT_APP_API_URL}/template/msg-temp-type-ids`,
                { headers: getApiHeaders() }
            );
            if (response.data) {
                setTemplates(response.data);
            } else {
                toast({
                    title: "Error",
                    description: "Failed to fetch templates",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            console.error('Error fetching templates:', error);
            const errorMessage = error.response?.data?.message || error.message || "Failed to fetch templates";
            toast({
                title: "Error",
                description: errorMessage,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleChannelSelect = (selectedOption) => {
        const channel = selectedOption ? selectedOption.value : null;
        const resolvedTemplate =
            channel && nodeConfig.templateDetails
                ? getTemplateByChannel(channel, nodeConfig.templateDetails)
                : nodeConfig.template;
        setNodeConfig({ ...nodeConfig, channel, template: resolvedTemplate });
    };

    const getTemplateByChannel = (channel, templateDetails) => {
        if (!templateDetails) return '';
        switch (String(channel)) {
            case '1': return templateDetails.smsBody || '';
            case '2': return templateDetails.whatsappBody || '';
            case '3': return templateDetails.emailHtml || templateDetails.emailTemplate || '';
            case '4': return templateDetails.voiceScript || '';
            case '5': return templateDetails.inAppBody || '';
            case '6': return templateDetails.pushBody || '';
            case 'smart': return templateDetails.masterMessageContent || '';
            default: return '';
        }
    };

    const handleTemplateSelect = (selectedOption) => {
        if (!selectedOption) {
            setSelectedTemplate(null);
            setNodeConfig({
                ...nodeConfig,
                templateId: null,
                templateName: null,
                template: null,
                templateDetails: null,
                availableChannels: [],
                channelIds: [],
                channel: null,
                variableMappings: {}
            });
            return;
        }

        const template = templates.data.find((t) => t.id === selectedOption.value);
        if (template) {
            setSelectedTemplate(template);
            let templateDetails = {};
            try {
                templateDetails = JSON.parse(template.message_combine);
            } catch (e) {
                console.error('Error parsing template details:', e);
            }

            const channelIds = template.channel_id ? JSON.parse(template.channel_id) : [];
            const availableChannels = Channels.filter(ch => channelIds.includes(ch.id));
            const existingChannelId = nodeConfig.channel;
            const channelStillAvailable = existingChannelId &&
                (existingChannelId === 'smart' || channelIds.includes(Number(existingChannelId)));

            setNodeConfig({
                ...nodeConfig,
                templateId: template.id,
                templateName: template.name,
                template: template.message,
                templateDetails,
                availableChannels,
                channelIds,
                channel: nodeConfig.templateId === template.id && channelStillAvailable ? existingChannelId : null,
                variableMappings: nodeConfig.templateId === template.id ? (nodeConfig.variableMappings || {}) : {}
            });
        }
    };

    // ── Variable picker helpers ───────────────────────────────────────────────
    const openVariablePicker = (varName) => {
        setActiveVariableKey(varName);
        setVariablePickerOpen(true);
    };

    const handleVariablePickerSelect = (varPath) => {
        if (!activeVariableKey) return;
        const newMappings = { ...nodeConfig.variableMappings };
        newMappings[activeVariableKey] = {
            ...(newMappings[activeVariableKey] || DEFAULT_MAPPING),
            sourceValue: `{{${varPath}}}`,
        };
        setNodeConfig({ ...nodeConfig, variableMappings: newMappings });
    };

    const isDynamic = (val) => val?.includes('{{') && val?.includes('}}');

    const updateMapping = (variable, updates) => {
        const newMappings = { ...nodeConfig.variableMappings };
        newMappings[variable] = { ...(newMappings[variable] || DEFAULT_MAPPING), ...updates };
        setNodeConfig({ ...nodeConfig, variableMappings: newMappings });
    };

    const templateOptions = templates?.data?.map((template) => ({
        value: template.id,
        label: `${template.name}`,
    })) || [];

    const channelOptions = [
        { value: 'smart', label: <span><Flex alignItems={"center"}><LuMerge style={{ marginRight: "8px", color: "#9810fa" }} />Smart Routing</Flex></span> },
        ...(nodeConfig.availableChannels?.map((channel) => ({
            value: String(channel.id),
            label: channel.name,
        })) || [])
    ];

    const selectedTemplateValue = nodeConfig.templateId
        ? templateOptions.find(opt => opt.value === parseInt(nodeConfig.templateId))
        : null;

    const selectedChannelValue = nodeConfig.channel
        ? channelOptions.find(opt => opt.value === String(nodeConfig.channel))
        : null;

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '32px',
            height: '32px',
            fontSize: '14px',
            borderColor: state.isFocused ? 'var(--brand-primary)' : '#E2E8F0',
            boxShadow: state.isFocused ? '0 0 0 1px var(--brand-primary)' : 'none',
            '&:hover': { borderColor: '#CBD5E0' }
        }),
        valueContainer: (provided) => ({ ...provided, height: '32px', padding: '0 8px' }),
        input: (provided) => ({ ...provided, margin: '0', padding: '0' }),
        indicatorsContainer: (provided) => ({ ...provided, height: '32px' }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '14px',
            backgroundColor: state.isSelected ? 'var(--brand-primary)' : state.isFocused ? 'var(--brand-tint)' : 'white',
            color: state.isSelected ? 'white' : '#2D3748',
            '&:active': { backgroundColor: 'var(--brand-primary)' }
        }),
        menu: (provided) => ({ ...provided, zIndex: 9999 })
    };

    const templateVariables = extractTemplateVariables(nodeConfig.template);

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Welcome Message"
                />
            </FormControl>

            <Divider />

            {/* Message Type Selection */}
            <FormControl>
                <FormLabel fontSize="sm">Message Type</FormLabel>
                <RadioGroup
                    value={nodeConfig.messageType || 'template'}
                    onChange={(value) => setNodeConfig({
                        ...nodeConfig,
                        messageType: value,
                        ...(value === 'freeform' ? {
                            templateId: null,
                            templateName: null,
                            template: null,
                            templateDetails: null,
                            variableMappings: {}
                        } : {
                            freeformMessage: ''
                        })
                    })}
                >
                    <VStack spacing={4} align="flex-start">
                        <Radio value="template">Template Message</Radio>
                        <Radio value="freeform">Freeform Message</Radio>
                    </VStack>
                </RadioGroup>
            </FormControl>

            <Divider />

            {nodeConfig.messageType === 'template' ? (
                <>
                    <FormControl>
                        <FormLabel fontSize="sm">Message Template</FormLabel>
                        <Select
                            value={selectedTemplateValue}
                            onChange={handleTemplateSelect}
                            options={templateOptions}
                            placeholder={loadingTemplates ? "Loading templates..." : "Select template"}
                            isDisabled={loadingTemplates}
                            isLoading={loadingTemplates}
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            noOptionsMessage={() => "No templates found"}
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Delivery Channel</FormLabel>
                        <Select
                            value={selectedChannelValue}
                            onChange={handleChannelSelect}
                            options={channelOptions}
                            placeholder="Select channel"
                            isDisabled={!selectedTemplate}
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            noOptionsMessage={() => "No channels available"}
                        />
                       
                        {(nodeConfig.channel && nodeConfig.channel !== 'smart') && (
                            <Box mt={2} p={2} bg="gray.50" borderRadius="md">
                                <FormControl>
                                    <FormLabel fontSize="sm">Template Content</FormLabel>
                                    <Textarea
                                        size="sm"
                                        value={nodeConfig.template || nodeConfig.templateDetails?.emailHtml || nodeConfig.templateDetails?.masterMessageContent || ''}
                                        readOnly
                                        fontSize="xs"
                                    />
                                </FormControl>
                            </Box>
                        )}

                        {nodeConfig.channel === 'smart' && (
                            <Box mt={2} p={3} bg="purple.50" border="1px" borderColor="purple.200" borderRadius="md">
                                <HStack spacing={2} mb={1}>
                                    <LuZap size={14} color="#9810fa" />
                                    <Text fontSize="xs" fontWeight="medium" color="purple.900">
                                        Smart Routing Enabled
                                    </Text>
                                </HStack>
                                <Text fontSize="xs" color="purple.700" mb={2}>
                                    Messages will automatically fallback to alternate channels if delivery fails.
                                </Text>
                            </Box>
                        )}
                    </FormControl>
                    {/* Template Variables Section */}
                    {nodeConfig.template && templateVariables.length > 0 && (
                        <>
                            <Divider />
                            <VStack spacing={3} align="stretch">
                                <Box>
                                    <FormLabel fontSize="sm" fontWeight="semibold">Template Variables</FormLabel>
                                    <Text fontSize="xs" color="gray.600">
                                        Configure how each variable will be populated
                                    </Text>
                                </Box>

                                {templateVariables.map((variable) => {
                                    const mapping = nodeConfig.variableMappings?.[variable] || { ...DEFAULT_MAPPING };

                                    return (
                                        <Box
                                            key={variable}
                                            p={3}
                                            bg="gray.50"
                                            border="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                        >
                                            <HStack spacing={2} mb={2}>
                                                <LuDatabase size={16} color="var(--brand-primary)" />
                                                <Tag>${variable}</Tag>
                                            </HStack>

                                            <VStack spacing={2} align="stretch">
                                                {/* Source type */}
                                                <ChakraSelect
                                                    value={mapping.sourceType}
                                                    onChange={(e) => updateMapping(variable, {
                                                        sourceType: e.target.value,
                                                        sourceValue: '',
                                                        eventName: '',
                                                        propertyName: '',
                                                        fallbackValue: '',
                                                    })}
                                                    size="sm"
                                                    fontSize="xs"
                                                >
                                                    <option value="fixed">Fixed Value</option>
                                                    <option value="flow_context">Flow Variable</option>
                                                </ChakraSelect>

                                                {/* Fixed Value */}
                                                {mapping.sourceType === 'fixed' && (
                                                    <Input
                                                        placeholder={`Enter value for ${variable}`}
                                                        value={mapping.sourceValue}
                                                        onChange={(e) => updateMapping(variable, { sourceValue: e.target.value })}
                                                        size="sm"
                                                        fontSize="xs"
                                                    />
                                                )}

                                                {/* Flow Variable */}
                                                {mapping.sourceType === 'flow_context' && (
                                                    <Box position="relative">
                                                        <Input
                                                            placeholder="Click {x} to pick a flow variable"
                                                            value={mapping.sourceValue}
                                                            onChange={(e) => updateMapping(variable, { sourceValue: e.target.value })}
                                                            size="sm"
                                                            fontSize="xs"
                                                            fontFamily="mono"
                                                            pr="36px"
                                                            bg={isDynamic(mapping.sourceValue) ? 'blue.50' : undefined}
                                                            borderColor={isDynamic(mapping.sourceValue) ? 'blue.200' : undefined}
                                                        />
                                                        <IconButton
                                                            aria-label="Pick flow variable"
                                                            icon={<LuBraces />}
                                                            size="xs"
                                                            variant="ghost"
                                                            color="var(--brand-primary)"
                                                            _hover={{ bg: 'blue.50' }}
                                                            position="absolute"
                                                            right={1}
                                                            top="50%"
                                                            transform="translateY(-50%)"
                                                            zIndex={1}
                                                            onClick={() => openVariablePicker(variable)}
                                                        />
                                                    </Box>
                                                )}

                                                {/* Mapping summary */}
                                                {mapping.sourceValue && (
                                                    <Box pt={1} borderTop="1px" borderColor="gray.200">
                                                        <Box bg="white" p={2} borderRadius="md" border="1px" borderColor="gray.300">
                                                            <Text fontSize="xs" color="gray.600" display="inline">
                                                                Mapped to:{' '}
                                                            </Text>
                                                            <Code fontSize="xs" colorScheme="blue">
                                                                {mapping.sourceType === 'fixed'
                                                                    ? `"${mapping.sourceValue}"`
                                                                    : mapping.sourceValue}
                                                            </Code>
                                                        </Box>
                                                    </Box>
                                                )}
                                            </VStack>
                                        </Box>
                                    );
                                })}
                            </VStack>
                        </>
                    )}
                </>
            ) : (
                <>
                    {/* Freeform Message Section */}
                    <FormControl>
                        <FormLabel fontSize="sm">Delivery Channel</FormLabel>
                        <Select
                            value={selectedChannelValue}
                            onChange={handleChannelSelect}
                            options={[
                                { value: 'smart', label: <span><Flex alignItems={"center"}><LuMerge style={{ marginRight: "8px", color: "#9810fa" }} />Smart Routing</Flex></span> },
                                ...Channels.map((channel) => ({
                                    value: String(channel.id),
                                    label: channel.name,
                                }))
                            ]}
                            placeholder="Select channel"
                            isClearable
                            isSearchable
                            styles={customSelectStyles}
                            noOptionsMessage={() => "No channels available"}
                        />

                        {nodeConfig.channel === 'smart' && (
                            <Box mt={2} p={3} bg="purple.50" border="1px" borderColor="purple.200" borderRadius="md">
                                <HStack spacing={2} mb={1}>
                                    <LuZap size={14} color="#9810fa" />
                                    <Text fontSize="xs" fontWeight="medium" color="purple.900">
                                        Smart Routing Enabled
                                    </Text>
                                </HStack>
                                <Text fontSize="xs" color="purple.700">
                                    Messages will automatically fallback to alternate channels if delivery fails.
                                </Text>
                            </Box>
                        )}
                    </FormControl>

                    <FormControl>
                        <FormLabel fontSize="sm">Message Content</FormLabel>
                        <Textarea
                            size="sm"
                            value={nodeConfig.freeformMessage || ''}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, freeformMessage: e.target.value })}
                            placeholder="Enter your message content here..."
                            minH="150px"
                        />
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Write your message directly without using a template
                        </Text>
                    </FormControl>
                </>
            )}

            <Divider />

            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    colorScheme="blue"
                    onClick={updateNodeData}
                    flex={1}
                    isDisabled={
                        nodeConfig.messageType === 'template'
                            ? !selectedTemplate
                            : !nodeConfig.channel || !nodeConfig.freeformMessage
                    }
                >
                    Update Node
                </Button>
            </HStack>

            {/* Variable Picker Drawer */}
            <VariablePickerDrawer
                isOpen={variablePickerOpen}
                onClose={() => setVariablePickerOpen(false)}
                onSelect={handleVariablePickerSelect}
                flowNodes={nodes}
            />
        </VStack>
    );
}
